import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { usePlanetStore } from '../store/planetStore';
import { usePlayerStore } from '../store/playerStore';
import { useUniverseStore } from '../store/universeStore';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { useNow } from '../hooks/useNow';
import { usePlanetResource } from '../hooks/usePlanetResource';
import { buildShips, cancelShipBuild, rushShipBuild, deliverShipGroup, type PlanetActionOptions } from '../services/planet.service';
import QueueItemCard from '../components/QueueItemCard';
import ShipGroupBuilder from '../components/ShipGroupBuilder';
import ShipCard, { SHIP_CATEGORY_ORDER, SHIP_CATEGORY_TITLES } from '../components/ShipCard';
import { getShipIcon } from '../utils/shipIcons';
import { formatCountdown } from '../utils/formatters';
import { Button } from '../components/ui/Button';
import { Chip } from '../components/ui/Chip';
import { Panel } from '../components/ui/Panel';
import { Skeleton } from '../components/ui/Skeleton';
import { linkButtonClasses } from '../components/ui/buttonStyles';
import { zoneAccentStyle } from '../components/planet/zoneAccent';
import { eventProgress } from '../utils/eventProgress';
import {
    BUILDING_NAMES,
    SHIP_DEFINITIONS,
    SHIP_BUILD_REFUND_MAX,
    SHIP_BUILD_REFUND_MIN,
    getShipDefinition,
    type PlayerRace,
    type ShipBuildLane,
    type ShipId,
    type TickCheckpointEventOf,
} from '@kaeliux/shared';
import {
    calculateFreePopulation,
    calculateRushCost,
    extractBuildingLevels,
    extractScienceLevels,
} from '@kaeliux/game-engine';

/**
 * Which shipyard lane(s) each race's Construction Queue splits into (issue
 * #91). Humans and Robots always render a single `default` panel — visually
 * identical to the pre-#91 page; Axons render two independent panels since
 * `computeShipBuildLane` (game-engine) splits their builds by lane.
 */
const LANES_FOR_RACE: Record<PlayerRace, ShipBuildLane[]> = {
    humans: ['default'],
    robots: ['default'],
    // Axons list `default` too, even though `computeShipBuildLane` never
    // assigns it to them: batches queued before #91 carry no `queueLane` and
    // bucket as `default`, and a lane with no events renders nothing anyway.
    // Without it those in-flight batches would be invisible — but still
    // consuming population — until they completed.
    axons: ['default', 'creation', 'mutation'],
};

const LANE_TITLES: Record<ShipBuildLane, string> = {
    default: 'Construction Queue',
    creation: 'Creation Queue',
    mutation: 'Mutation Queue',
    // Team-planet-only per-race FIFOs (never rendered on a normal planet's
    // Factory page — `LANES_FOR_RACE` above never selects them here) —
    // included so `ShipBuildLane`'s full union stays exhaustive.
    humans: 'Humans Queue',
    robots: 'Robots Queue',
    axons: 'Axons Queue',
};

const REFUND_RANGE_LABEL = `${SHIP_BUILD_REFUND_MIN * 100}–${SHIP_BUILD_REFUND_MAX * 100}%`;

interface QueuedShipRowProps {
    planetId: string;
    race: PlayerRace;
    event: TickCheckpointEventOf<'shipBuild'>;
    now: number;
    options: PlanetActionOptions;
}

/**
 * One row in the "Construction Queue" panel for a standalone (non-grouped)
 * batch — mirrors SciencesPage's QueuedResearchRow. A Robots delivery group
 * (issue #91, `groupId` set) never reaches this component — see
 * `QueuedShipGroupRow`, which renders every item in a group as one combined
 * card instead.
 */
function QueuedShipRow({ planetId, race, event, now, options }: QueuedShipRowProps) {
    const { openDialog } = useConfirmDialog();
    const [isCancelling, setIsCancelling] = useState(false);
    const [isRushing, setIsRushing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const darkMatter = usePlayerStore((s) => s.premium?.darkMatter ?? 0);

    const def = getShipDefinition(race, event.eventDetails.shipType);
    const startMs = event.eventDetails.startDate.getTime();
    const endMs = event.endDate.getTime();
    const started = startMs <= now;
    const remainingMs = endMs - now;
    // A not-yet-started batch only has its OWN duration collapsed to zero by
    // a rush — it still waits for whatever's ahead of it in the lane — so
    // pricing it off the full remaining-to-completion time (which includes
    // that wait) would charge for wall-clock time the rush can't actually
    // remove (round 3 review, finding #2).
    const rushCost = calculateRushCost(started ? remainingMs / 1_000 : event.eventDetails.durationSeconds);
    const alreadyRushed = event.eventDetails.durationSeconds === 0;
    const progress = eventProgress(startMs, endMs, now);

    async function doCancel() {
        setIsCancelling(true);
        setError(null);
        try {
            const updated = await cancelShipBuild(planetId, event.eventId, options);
            usePlanetStore.setState({ currentPlanet: updated });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Cancel failed');
        } finally {
            setIsCancelling(false);
        }
    }

    async function doRush() {
        setIsRushing(true);
        setError(null);
        try {
            const { planet: updated, premium } = await rushShipBuild(planetId, event.eventId, options);
            usePlanetStore.setState({ currentPlanet: updated });
            usePlayerStore.setState({ premium });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Rush failed');
        } finally {
            setIsRushing(false);
        }
    }

    function requestCancel() {
        openDialog({
            title: `Cancel ${def.name} construction?`,
            message: `Finished ships stay in your fleet; the unbuilt remainder is refunded at ${REFUND_RANGE_LABEL} of its cost.`,
            confirmLabel: 'Cancel construction',
            danger: true,
            onConfirm: () => { doCancel().catch(() => { }); },
        });
    }

    function requestRush() {
        openDialog({
            title: `Rush ${def.name} construction?`,
            message: started
                ? `Instantly complete this for ${rushCost} Dark Matter. This is non-refundable.`
                : `Remove this batch's own build time for ${rushCost} Dark Matter. It will still wait for the batch ahead of it in the lane. This is non-refundable.`,
            confirmLabel: 'Rush',
            onConfirm: () => { doRush().catch(() => { }); },
        });
    }

    return (
        <QueueItemCard
            image={
                <div className="w-full h-full rounded-tile border border-hairline bg-surface-void/60 flex items-center justify-center overflow-hidden">
                    <img src={getShipIcon(race, def.id)} alt="" className="w-full h-full object-contain" />
                </div>
            }
            overlayLabel={`× ${event.eventDetails.quantity}`}
            name={def.name}
            statusText={`${started ? 'Building…' : 'Queued —'} ${formatCountdown(remainingMs)}`}
            progress={progress}
            error={error}
            isCancelling={isCancelling}
            onCancel={requestCancel}
            imageBoxClassName="w-20 aspect-[4/3]"
            rushCost={rushCost}
            isRushing={isRushing}
            rushDisabled={darkMatter < rushCost || alreadyRushed}
            rushDisabledReason={alreadyRushed ? 'Rushed' : undefined}
            onRush={requestRush}
        />
    );
}

interface QueuedShipGroupRowProps {
    planetId: string;
    race: PlayerRace;
    events: TickCheckpointEventOf<'shipBuild'>[];
    now: number;
    options: PlanetActionOptions;
}

/**
 * Robots-only (issue #91): one combined card for an entire delivery group —
 * every item shares a single timer, a single Cancel, and a single
 * "Deliver Now" action instead of each item rendering its own queue card.
 * `events` is every pending batch sharing the same `groupId`, always ≥
 * `SHIP_GROUP_MIN_ITEMS`.
 */
function QueuedShipGroupRow({ planetId, race, events, now, options }: QueuedShipGroupRowProps) {
    const { openDialog } = useConfirmDialog();
    const [isCancelling, setIsCancelling] = useState(false);
    const [isDelivering, setIsDelivering] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const groupId = events[0]!.eventDetails.groupId!;
    const deliverAt = events[0]!.eventDetails.deliverAt!;
    // The group as a whole is "built" once its LAST item finishes — the same
    // instant `deliverAt` was computed from — not when the first one does.
    const latestEndMs = Math.max(...events.map((e) => e.endDate.getTime()));
    const groupReady = now >= latestEndMs;
    // Once any item has finished, the group can no longer be cancelled as a
    // unit (mirrors cancelShipBuildEvent's per-item rule) — cancelling a
    // group is all-or-nothing here, not a partial cancel of the unfinished
    // items only.
    const canCancel = events.every((e) => now < e.endDate.getTime());
    const remainingMs = groupReady ? deliverAt.getTime() - now : latestEndMs - now;

    const items = events
        .slice()
        .sort((a, b) => a.eventId - b.eventId)
        .map((event) => ({
            eventId: event.eventId,
            def: getShipDefinition(race, event.eventDetails.shipType),
            quantity: event.eventDetails.quantity,
        }));

    async function doCancel() {
        setIsCancelling(true);
        setError(null);
        try {
            // One request per item — there is no atomic group-cancel endpoint,
            // so commit each result as it lands. Without this, a failure on
            // item 2 would discard item 1's already-committed cancellation and
            // leave the card rendering a batch the server no longer has.
            for (const event of events) {
                usePlanetStore.setState({ currentPlanet: await cancelShipBuild(planetId, event.eventId, options) });
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Cancel failed');
        } finally {
            setIsCancelling(false);
        }
    }

    function requestCancel() {
        if (!canCancel) return;
        openDialog({
            title: 'Cancel this delivery group?',
            message: `Every item in the group is cancelled; the unbuilt remainder of each is refunded at ${REFUND_RANGE_LABEL} of its cost.`,
            confirmLabel: 'Cancel group',
            danger: true,
            onConfirm: () => { doCancel().catch(() => { }); },
        });
    }

    async function doDeliver() {
        setIsDelivering(true);
        setError(null);
        try {
            const updated = await deliverShipGroup(planetId, groupId, options);
            usePlanetStore.setState({ currentPlanet: updated });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Delivery failed');
        } finally {
            setIsDelivering(false);
        }
    }

    return (
        <div
            data-testid="group-queue-card"
            className="flex flex-col gap-2 rounded-tile border border-status-warning/40 bg-status-warning/10 hover:border-status-warning transition-colors px-3 py-2 w-fit max-w-full shrink-0"
        >
            <div className="flex flex-wrap gap-3">
                {items.map(({ eventId, def, quantity }) => (
                    <div key={eventId} className="flex flex-col items-center gap-1 w-16" title={`${quantity} × ${def.name}`}>
                        <div className="relative w-10 h-10 rounded-tile border border-hairline bg-surface-void/60 flex items-center justify-center overflow-hidden shrink-0">
                            <img src={getShipIcon(race, def.id)} alt="" className="w-full h-full object-contain" />
                        </div>
                        <span className="text-micro text-content-primary text-center leading-tight truncate w-full">{def.name}</span>
                        <span className="text-label text-content-secondary">× {quantity.toLocaleString()}</span>
                    </div>
                ))}
            </div>
            <span className="text-micro text-content-muted">
                {groupReady
                    ? `Ready — docks automatically in ${formatCountdown(remainingMs)} if not delivered`
                    : `Building… ${formatCountdown(remainingMs)}`}
            </span>
            {error && <Chip tone="danger">{error}</Chip>}
            <div className="flex gap-2 self-start">
                {groupReady && (
                    <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={() => { doDeliver().catch(() => { }); }}
                        disabled={isDelivering}
                    >
                        {isDelivering ? 'Delivering…' : 'Deliver Now'}
                    </Button>
                )}
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={requestCancel}
                    disabled={isCancelling || !canCancel}
                >
                    {isCancelling ? 'Cancelling…' : 'Cancel'}
                </Button>
            </div>
        </div>
    );
}

function ShipyardPageContent({ planetId, race }: { planetId: string; race: PlayerRace }) {
    const { state: { planet, isLoading } } = usePlanetResource();
    const { sciences, shipUpgrades } = usePlayerStore();
    const { resourceMultiplier, speedMultiplier, sunAges } = useUniverseStore();
    const now = useNow();

    // Robots-only (issue #91): ships staged for a delivery group but not
    // yet submitted via ShipGroupBuilder — ephemeral compose-in-progress UI
    // state, not server data, so plain component state.
    const [groupCart, setGroupCart] = useState<Partial<Record<ShipId, number>>>({});
    const addToGroup = (shipId: ShipId, quantity: number) => {
        setGroupCart((prev) => ({ ...prev, [shipId]: (prev[shipId] ?? 0) + quantity }));
    };
    const removeFromGroup = (shipId: ShipId) => {
        setGroupCart((prev) => {
            const next = { ...prev };
            delete next[shipId];
            return next;
        });
    };

    const shipUpgradeLevels = useMemo(
        () => (shipUpgrades ?? {}) as Partial<Record<ShipId, number>>,
        [shipUpgrades]
    );

    const options: PlanetActionOptions = useMemo(
        () => ({ resourceMultiplier, sunAges, speedMultiplier }),
        [resourceMultiplier, sunAges, speedMultiplier]
    );

    const scienceLevels = useMemo(
        () => extractScienceLevels(sciences ?? undefined),
        [sciences]
    );

    const buildingLevels = useMemo(
        () => (planet ? extractBuildingLevels(planet.buildings) : ({} as ReturnType<typeof extractBuildingLevels>)),
        [planet]
    );

    const shipQueue = useMemo(
        () => (planet?.tickCheckpoints ?? []).filter(
            (e): e is TickCheckpointEventOf<'shipBuild'> => e.eventType === 'shipBuild' && !e.cancelled
        ),
        [planet]
    );

    const shipQueueByLane = useMemo(() => {
        const map = new Map<ShipBuildLane, TickCheckpointEventOf<'shipBuild'>[]>();
        for (const event of shipQueue) {
            const lane = event.eventDetails.queueLane ?? 'default';
            const laneEvents = map.get(lane) ?? [];
            laneEvents.push(event);
            map.set(lane, laneEvents);
        }
        return map;
    }, [shipQueue]);

    const freePopulation = planet
        ? calculateFreePopulation(race, planet.resources.population.capacity, planet.ships, planet.tickCheckpoints, planet.shipsInFlight)
        : 0;

    if (isLoading || !planet || sciences === null) {
        return (
            <div className="min-h-screen bg-space-900 flex flex-col items-center justify-center gap-3 px-4 w-full max-w-md mx-auto" style={zoneAccentStyle('military')}>
                <span className="text-title text-content-muted">{`Loading ${BUILDING_NAMES.shipyard}…`}</span>
                <Skeleton variant="block" className="w-full h-24" />
                <Skeleton variant="block" className="w-full h-24" />
            </div>
        );
    }

    if ((buildingLevels.shipyard ?? 0) === 0) {
        return <Navigate to="/zone/military?highlight=shipyard" replace />;
    }

    const dockedShips = (Object.entries(planet.ships ?? {}) as [string, number][])
        .filter(([, count]) => count > 0)
        .map(([id, count]) => ({ def: getShipDefinition(race, Number(id) as ShipId), count }));

    return (
        <div className="bg-transparent flex flex-col" style={zoneAccentStyle('military')}>
            <main className="flex-1 w-full max-w-7xl mx-auto px-4 py-4 flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <h1 className="text-display text-content-primary">{BUILDING_NAMES.shipyard}</h1>
                    <div className="flex items-center gap-3">
                        <span className="text-body text-content-muted">
                            Free population: {freePopulation.toLocaleString()} / {planet.resources.population.capacity.toLocaleString()}
                        </span>
                        <Link to="/planet" className={linkButtonClasses('secondary')}>← Back to Planet</Link>
                    </div>
                </div>

                {LANES_FOR_RACE[race].map((lane) => {
                    const laneEvents = shipQueueByLane.get(lane) ?? [];
                    if (laneEvents.length === 0) return null;

                    // Robots delivery groups (issue #91) render as one
                    // combined card per groupId instead of one per item —
                    // partition the lane's events accordingly.
                    const groups = new Map<number, TickCheckpointEventOf<'shipBuild'>[]>();
                    const standalone: TickCheckpointEventOf<'shipBuild'>[] = [];
                    for (const event of laneEvents) {
                        const groupId = event.eventDetails.groupId;
                        if (groupId) {
                            const members = groups.get(groupId) ?? [];
                            members.push(event);
                            groups.set(groupId, members);
                        } else {
                            standalone.push(event);
                        }
                    }

                    return (
                        <Panel key={lane} variant="solid" data-testid="queue-lane-panel">
                            <h2 className="text-title text-content-secondary mb-1">{LANE_TITLES[lane]}</h2>
                            <div className="flex flex-wrap gap-3">
                                {[...groups.entries()].map(([groupId, groupEvents]) => (
                                    <QueuedShipGroupRow
                                        key={groupId}
                                        planetId={planetId}
                                        race={race}
                                        events={groupEvents}
                                        now={now}
                                        options={options}
                                    />
                                ))}
                                {standalone.map((event) => (
                                    <QueuedShipRow
                                        key={event.eventId}
                                        planetId={planetId}
                                        race={race}
                                        event={event}
                                        now={now}
                                        options={options}
                                    />
                                ))}
                            </div>
                        </Panel>
                    );
                })}

                {race === 'robots' && Object.keys(groupCart).length > 0 && (
                    <ShipGroupBuilder
                        planetId={planetId}
                        race={race}
                        cart={groupCart}
                        onRemove={removeFromGroup}
                        onQueued={() => setGroupCart({})}
                        options={options}
                    />
                )}

                {dockedShips.length > 0 && (
                    <Panel variant="solid" data-testid="docked-fleet-panel">
                        <h2 className="text-title text-content-secondary mb-1">Docked Fleet</h2>
                        <div className="flex flex-wrap gap-x-6 gap-y-1 text-body text-content-secondary">
                            {dockedShips.map(({ def, count }) => (
                                <span key={def.id}>
                                    <span className="font-semibold text-content-primary">{def.name}</span>
                                    <span className="text-content-muted"> × {count.toLocaleString()}</span>
                                </span>
                            ))}
                        </div>
                    </Panel>
                )}

                {SHIP_CATEGORY_ORDER.map((category) => {
                    const ships = Object.values(SHIP_DEFINITIONS[race]).filter((def) => def.category === category);
                    return (
                        <div key={category} className="flex flex-col gap-3">
                            <h2 className="text-heading text-content-secondary">{SHIP_CATEGORY_TITLES[category]}</h2>
                            <div className="flex flex-col gap-3">
                                {ships.map((def) => (
                                    <ShipCard
                                        key={def.id}
                                        def={def}
                                        planet={planet}
                                        race={race}
                                        scienceLevels={scienceLevels}
                                        buildingLevels={buildingLevels}
                                        freePopulation={freePopulation}
                                        speedMultiplier={speedMultiplier}
                                        upgradeLevel={shipUpgradeLevels[def.id] ?? 0}
                                        onBuild={async (quantity) => {
                                            const updated = await buildShips(planet.id, def.id, quantity, options);
                                            usePlanetStore.setState({ currentPlanet: updated });
                                        }}
                                        onAddToGroup={race === 'robots' ? addToGroup : undefined}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </main>
        </div>
    );
}

export default function ShipyardPage() {
    const { profile } = useAuthStore();
    const activePlanetId = usePlanetStore((s) => s.activePlanetId);

    const planetId = activePlanetId ?? profile?.mainPlanetId ?? '';

    if (!profile || !planetId || !profile.race) {
        return (
            <div className="min-h-screen bg-space-900 flex items-center justify-center">
                <span className="text-title text-content-muted">Loading…</span>
            </div>
        );
    }

    return (
        /* Keyed on planetId so switching planets remounts the page —
           otherwise the delivery-group cart staged on the previous planet
           survives the switch and submits against the new one. */
        <ShipyardPageContent key={planetId} planetId={planetId} race={profile.race} />
    );
}
